<template>
  <section
    v-if="hasElements"
    id="game-elements-section"
    class="game-elements-section"
    :aria-label="$t('gameElements.sectionLabel')"
  >
    <div class="game-elements-section__container">
      <div
        class="game-elements-section__header"
        v-if="displayTitle || displaySubtitle"
      >
        <h2
          v-if="displayTitle"
          class="game-elements-section__title"
        >
          {{ displayTitle }}
        </h2>
        <p
          v-if="displaySubtitle"
          class="game-elements-section__subtitle"
        >
          {{ displaySubtitle }}
        </p>
      </div>

      <div class="game-elements-section__grid">
        <div
          v-for="(element, index) in displayElements"
          :key="`element-${index}`"
          class="game-elements-section__element"
          :style="{ '--animation-delay': `${index * 0.15}s` }"
        >
          <div class="game-element">
            <div
              class="game-element__media"
              v-if="element.imageUrl"
            >
              <img
                :src="element.imageUrl"
                :alt="element.title || displayTitle || 'Game feature image'"
                class="game-element__image"
                loading="lazy"
              >
            </div>

            <div
              class="game-element__icon-container"
              v-if="element.iconUrl && !element.imageUrl"
            >
              <div class="game-element__icon">
                <img
                  :src="element.iconUrl"
                  :alt="element.title || displayTitle || 'Game feature icon'"
                  class="game-element__icon-image"
                  loading="lazy"
                >
              </div>
              <div class="game-element__icon-bg"></div>
            </div>

            <div class="game-element__content">
              <h3
                v-if="element.title"
                class="game-element__title"
              >
                {{ element.title }}
              </h3>
              <p
                v-if="element.description"
                class="game-element__description"
              >
                {{ element.description }}
              </p>

              <ul
                v-if="element.features.length > 0"
                class="game-element__features"
              >
                <li
                  v-for="(feature, featureIndex) in element.features"
                  :key="`feature-${index}-${featureIndex}`"
                  class="game-element__feature"
                >
                  {{ feature }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div
        class="game-elements-section__footer"
        v-if="displayFooterText"
      >
        <p class="game-elements-section__footer-text">
          {{ displayFooterText }}
        </p>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import useGameElements from './GameElements.ts'

interface GameElement {
  title: string
  description: string
  iconUrl?: string
  imageUrl?: string
  features: string[]
}

interface GameElementsContent {
  gameElementsTitle?: string
  gameElementsSubtitle?: string
  gameElementsFooterText?: string
  gameElements?: GameElement[]
}

interface Props {
  content: GameElementsContent
}

const props = withDefaults(defineProps<Props>(), {
  content: () => ({})
})

const gameElementsState = useGameElements(props)

const displayTitle = computed(() => {
  return gameElementsState.title.value || props.content.gameElementsTitle || ''
})

const displaySubtitle = computed(() => {
  return gameElementsState.description.value || props.content.gameElementsSubtitle || ''
})

const displayFooterText = computed(() => {
  return props.content.gameElementsFooterText || ''
})

const displayElements = computed<GameElement[]>(() => {
  if (gameElementsState.elements.value && gameElementsState.elements.value.length > 0) {
    return gameElementsState.elements.value as GameElement[]
  }

  if (props.content.gameElements && props.content.gameElements.length > 0) {
    return props.content.gameElements
  }

  return []
})

const hasElements = computed(() => displayElements.value.length > 0)

defineExpose({ hasElements })
</script>

<style lang="scss" scoped>
@use './GameElements.scss';
</style>
